import * as v from 'valibot';

/**
 * What reef knows about a repository.
 *
 * An agent that greps is an agent that confidently misses the second call site,
 * and "confidently" is the part that costs. The index exists so a step can ask
 * a question — where is this defined, what calls it, what does this module do —
 * and get an answer that accounts for the whole tree rather than for whatever
 * the first search happened to return.
 *
 * Addressed by `(repo, ref)` and rebuilt on push. The previous commit's rows are
 * deleted rather than kept: an index of a commit nobody is on is worse than no
 * index, because it answers.
 */
export const SymbolKind = v.picklist([
  'function',
  'class',
  'interface',
  'type',
  'const',
  'component',
  'route',
  'table',
]);
export type SymbolKind = v.InferOutput<typeof SymbolKind>;

export const CodeSymbol = v.object({
  name: v.string(),
  kind: SymbolKind,
  path: v.string(),
  line: v.pipe(v.number(), v.integer()),
  /** The signature as written, so an answer can quote rather than paraphrase. */
  signature: v.nullable(v.string()),
  exported: v.boolean(),
});
export type CodeSymbol = v.InferOutput<typeof CodeSymbol>;

export const CodeIndexSummary = v.object({
  repo: v.string(),
  ref: v.string(),
  commitSha: v.string(),
  fileCount: v.pipe(v.number(), v.integer()),
  symbolCount: v.pipe(v.number(), v.integer()),
  builtAt: v.string(),
});
export type CodeIndexSummary = v.InferOutput<typeof CodeIndexSummary>;

/**
 * A file as the index records it.
 *
 * `summary` is one sentence per file rather than a full description: the whole
 * value of an index over a grep is that it fits in a context window, and a
 * paragraph per file for a large repository does not.
 */
export const CodeFile = v.object({
  path: v.string(),
  language: v.string(),
  lines: v.pipe(v.number(), v.integer()),
  summary: v.nullable(v.string()),
  imports: v.array(v.string()),
});
export type CodeFile = v.InferOutput<typeof CodeFile>;

/** Extensions worth indexing, and what to call each one. */
export const LANGUAGES: Record<string, string> = {
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  py: 'python',
  go: 'go',
  rs: 'rust',
  rb: 'ruby',
  java: 'java',
  kt: 'kotlin',
  sql: 'sql',
  sh: 'shell',
};

/**
 * Paths never worth indexing.
 *
 * Not just noise: a `node_modules` big enough to dominate the symbol table
 * makes every lookup return a dependency's `parse` rather than yours, which is
 * the specific way a code index stops being useful.
 */
const IGNORED = [
  /(^|\/)node_modules\//,
  /(^|\/)\.git\//,
  /(^|\/)dist\//,
  /(^|\/)build\//,
  /(^|\/)\.wrangler\//,
  /(^|\/)coverage\//,
  /\.min\.(js|css)$/,
  /\.(png|jpe?g|gif|svg|ico|woff2?|ttf|pdf|zip|tar|gz)$/i,
];

export function isIndexable(path: string): boolean {
  if (IGNORED.some((pattern) => pattern.test(path))) return false;
  const extension = path.split('.').pop()?.toLowerCase() ?? '';
  return extension in LANGUAGES;
}

export function languageFor(path: string): string {
  return LANGUAGES[path.split('.').pop()?.toLowerCase() ?? ''] ?? 'text';
}
